import type { ReactNode } from 'react';
import { useEffect } from 'react';
import type { FilterMainComponentProps } from '@/app/common/filters/core/filterComponentType.ts';
import type { FilterDataOption } from '@/app/common/filters/core/filtersTypes.ts';
import {
  applyDefaultValueWhenNoSelected,
  multiSelectDataOf,
  optionIsDisabled,
  toggleValues,
} from '@/app/common/filters/core/filterOptionSelection.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';
import { FilterTile, type TileVariant } from '@/app/common/filters/surfaces/forms/tiles/FilterTile.tsx';
import { FilterTileRow, type TilesPerRow } from '@/app/common/filters/surfaces/forms/tiles/FilterTileRow.tsx';

export type TileFilterConfig = {
  idPrefix: string;
  perRow: TilesPerRow;
  variant: TileVariant;
  renderContent: (option: FilterDataOption) => ReactNode;
  // The tile shows a glyph or an icon, so a filter can name it the way a list row would announce it.
  ariaLabel?: (option: FilterDataOption, t: TranslationFunction) => string;
  sortOptions?: (options: FilterDataOption[]) => FilterDataOption[];
};

/**
 * A filter's All filters main component, rendered as rectangle tiles. Fixed layout, so `columns` is
 * ignored rather than routed through getNumberOfColumns.
 */
export const createFilterTilesMain = ({
  idPrefix,
  perRow,
  variant,
  renderContent,
  ariaLabel,
  sortOptions,
}: TileFilterConfig) =>
  function FilterTilesMain({ filter, isLoading, handleFilterChanged }: FilterMainComponentProps) {
    const { options } = multiSelectDataOf(filter);
    const { useEvoFilters } = useFiltersUiData();
    const { t } = useTranslation();

    useEffect(() => {
      applyDefaultValueWhenNoSelected(filter, handleFilterChanged);
    }, [filter, handleFilterChanged]);

    const onChange = (value: string) => handleFilterChanged([toggleValues(filter, [value])]);

    // An "Any" option is a reset control, not a category; the designs hide it in every tile frame.
    // Display-only: toggleValues still receives the whole filter.
    const shown = options.filter((option) => !option.isDefault);
    const ordered = sortOptions ? sortOptions(shown) : shown;

    return (
      <FilterTileRow $perRow={perRow}>
        {ordered.map((option) => (
          <FilterTile
            key={option.value}
            id={`${idPrefix}-${option.value}`}
            ariaLabel={ariaLabel?.(option, t)}
            variant={variant}
            selected={!!option.selected}
            disabled={optionIsDisabled({ option, filter, isLoading, useEvoFilters })}
            onChange={() => onChange(option.value)}
          >
            {renderContent(option)}
          </FilterTile>
        ))}
      </FilterTileRow>
    );
  };
